import React, { useContext, useMemo, useState } from 'react'
import Accordion from 'react-bootstrap/Accordion'
import { useAccordionButton } from 'react-bootstrap/AccordionButton'
import AccordionContext from 'react-bootstrap/AccordionContext'
import Card from 'react-bootstrap/Card'

import { graphql } from 'gatsby'
import { useKeenSlider } from 'keen-slider/react'

import SlideBlock from './components/slide-block/slide-block'
import Anchor from '@/components/anchor/anchor'

import arrowLeft from './arrow-left.svg'
import arrowRight from './arrow-right.svg'
import minus from './minus.svg'
import plus from './plus.svg'

import 'keen-slider/keen-slider.min.css'

import * as style from './slides-section.module.scss'

const Arrow = (props) => {
  return (
    <div onClick={props.onClick}>
      {props.left && <img src={arrowLeft} alt="left" />}
      {!props.left && <img src={arrowRight} alt="right" />}
    </div>
  )
}

const CustomToggle = ({ children, eventKey }) => {
  const { activeEventKey } = useContext(AccordionContext)
  const decoratedOnClick = useAccordionButton(eventKey)
  const isCurrentEventKey = activeEventKey === eventKey

  return (
    <span
      type="button"
      className={`${style.text} b400 m-0 d-flex justify-content-between align-items-center`}
      onClick={decoratedOnClick}
    >
      {children}{' '}
      {isCurrentEventKey ? (
        <img src={minus} alt="minus" />
      ) : (
        <img src={plus} alt="plus" />
      )}
    </span>
  )
}

const ContentMobile = ({
  anchor,
  title,
  index,
  slides = [],
  papers = [],
  sliderRefMobile,
  instanceRefMobile,
  currentSlide,
  loaded,
  bgStyle
}) => {
  let customIndex = 0

  const isOpenedByDefault = useMemo(() => {
    if (typeof window === 'undefined') return null

    return window.location?.hash === `#${anchor}` ? String(index) : ''
  }, [anchor, index])

  return (
    <div className={style.contentMobile}>
      <Accordion defaultActiveKey={isOpenedByDefault}>
        <Card className={style.card}>
          <Card.Header className={`${style.cardHeader} ${bgStyle}`}>
            <div className={style.title}>
              <div className="container">
                <div className="row">
                  <div className="col-12">
                    <h2 className="h100 text-uppercase p-0">
                      <CustomToggle eventKey={String(index)}>
                        {index + 1}. {title}
                      </CustomToggle>
                    </h2>
                  </div>
                </div>
              </div>
            </div>
          </Card.Header>
          <Accordion.Collapse eventKey={String(index)}>
            <Card.Body className={`${style.cardBody} ${bgStyle}`}>
              <div
                className={`${style.sliderWrapper} ${
                  loaded ? style.loaded : ''
                }`}
              >
                {loaded && instanceRefMobile.current && (
                  <div className={style.arrows}>
                    <Arrow
                      className={style.arrow}
                      left
                      onClick={(e) =>
                        e.stopPropagation() || instanceRefMobile.current?.prev()
                      }
                      disabled={currentSlide === 0}
                    />

                    <Arrow
                      className={style.arrow}
                      onClick={(e) =>
                        e.stopPropagation() || instanceRefMobile.current?.next()
                      }
                      disabled={
                        currentSlide ===
                        instanceRefMobile.current.track.details.slides.length -
                          1
                      }
                    />
                  </div>
                )}
                <div ref={sliderRefMobile} className="keen-slider">
                  {slides.map((slide, i) => {
                    customIndex = slide.isContinued
                      ? customIndex
                      : customIndex + 1
                    return (
                      <SlideBlock
                        key={`slide-mobile-${i}`}
                        anchor={anchor}
                        slide={slide}
                        i={i}
                        mobile
                        currentSlide={currentSlide}
                        customIndex={customIndex}
                        papers={papers}
                      />
                    )
                  })}
                </div>
              </div>
            </Card.Body>
          </Accordion.Collapse>
        </Card>
      </Accordion>
    </div>
  )
}

const ContentDesktop = ({
  anchor,
  bgStyle,
  index,
  title,
  loaded,
  currentSlide,
  instanceRef,
  slides,
  papers,
  sliderRef
}) => {
  let customIndex = 0

  return (
    <div className={style.contentDesktop}>
      <div className={`${style.title} ${bgStyle}`}>
        <div className="container">
          <div className="row">
            <div className="col-12">
              <h2 className="h100 text-uppercase p-0">{title}</h2>
            </div>
          </div>
        </div>
      </div>
      <div className={`${style.sliderWrapper} ${loaded ? style.loaded : ''}`}>
        {!!loaded && instanceRef?.current && (
          <div className={style.arrowsWrapper}>
            <div
              className={style.arrowLeft}
              onClick={(e) =>
                e.stopPropagation() || instanceRef.current?.prev()
              }
              disabled={currentSlide === 0}
            />

            <div
              className={style.arrowRight}
              onClick={(e) =>
                e.stopPropagation() || instanceRef.current?.next()
              }
              disabled={
                currentSlide ===
                instanceRef?.current?.track?.details?.slides?.length - 1
              }
            />
          </div>
        )}
        <div ref={sliderRef} className={`keen-slider ${bgStyle}`}>
          {slides.length > 0 &&
            slides.map((slide, i) => {
              customIndex = slide.isContinued ? customIndex : customIndex + 1

              return (
                <SlideBlock
                  key={`slide-desktop-${i}`}
                  anchor={anchor}
                  slide={slide}
                  i={i}
                  customIndex={customIndex}
                  papers={papers}
                />
              )
            })}
        </div>
      </div>
    </div>
  )
}
const SlideSection = ({ anchor, title, index, slides = [], papers = [] }) => {
  const [currentSlide, setCurrentSlide] = useState(0)
  const [loaded, setLoaded] = useState(false)

  const queryString = typeof window !== 'undefined' && window.location?.search
  const urlParams = new URLSearchParams(queryString)

  const sInitial = urlParams.get('slide')

  const ResizePlugin = (slider) => {
    const observer = new ResizeObserver(function () {
      slider.update()
      index === Number(sInitial)
        ? slider.moveToIdx(Number(sInitial))
        : slider.moveToIdx(0)
    })

    slider.on('created', () => {
      observer.observe(slider.container)
    })

    slider.on('destroyed', () => {
      observer.unobserve(slider.container)
    })
  }

  const bgStyle = `${style.bgSlide} ${style[`bgSlide${index + 1}`]}`

  const [sliderRef, instanceRef] = useKeenSlider(
    {
      slides: {
        perView: 1
      },
      slideChanged(slider) {
        setCurrentSlide(slider.track.details.rel)
      },
      created() {
        setLoaded(true)
      }
    },
    [ResizePlugin]
  )

  const [sliderRefMobile, instanceRefMobile] = useKeenSlider(
    {
      slides: {
        perView: 1
      },
      slideChanged(slider) {
        setCurrentSlide(slider.track.details.rel)
      },
      created() {
        setLoaded(true)
      }
    },
    [ResizePlugin]
  )

  return (
    <section className={`${style.section}`}>
      <Anchor anchor={anchor} />
      <ContentDesktop
        anchor={anchor}
        bgStyle={bgStyle}
        index={index}
        title={title}
        loaded={loaded}
        currentSlide={currentSlide}
        instanceRef={instanceRef}
        slides={slides}
        papers={papers}
        sliderRef={sliderRef}
      />
      <ContentMobile
        anchor={anchor}
        title={title}
        index={index}
        slides={slides}
        papers={papers}
        loaded={loaded}
        sliderRefMobile={sliderRefMobile}
        instanceRefMobile={instanceRefMobile}
        currentSlide={currentSlide}
      />
    </section>
  )
}

export default SlideSection

export const query = graphql`
  fragment DatoCmsDataSlide on DatoCmsDataSlide {
    title
    data
    dataType
    variant
    preContent {
      value
    }
    content {
      value
    }
  }

  fragment DatoCmsVideoSlide on DatoCmsVideoSlide {
    title
    variant
    isContinued
    preContent {
      value
    }
    content {
      value
    }
    videos {
      __typename
      ... on DatoCmsVideo {
        ...VideoInline
      }
    }
  }

  fragment DatoCmsSlidesSection on DatoCmsSlidesSection {
    __typename
    anchor
    title
    papers {
      ... on DatoCmsPctPaper {
        id: originalId
      }
    }
    slides {
      __typename
      ... on DatoCmsBasicSlide {
        title
        preContent {
          value
        }
        content {
          value
        }
        imageDesktop {
          isImage
          url
          gatsbyImageData(width: 650, height: 850)
        }
        imageMobile {
          isImage
          url
          gatsbyImageData(width: 650, height: 850)
        }
        imagePosition
        isContinued
        isQuote
      }
      ... on DatoCmsColumnsSlide {
        __typename
        title
        columns {
          ... on DatoCmsSlideColumn {
            content {
              value
              blocks {
                __typename
                ... on DatoCmsTableContent {
                  id: originalId
                  ...DatoCmsTableContent
                }
                ... on DatoCmsColumnCard {
                  id: originalId
                  title
                  text
                  image {
                    url
                    gatsbyImageData(height: 64)
                  }
                }
              }
            }
          }
        }
        imageDesktop {
          isImage
          url
          gatsbyImageData(width: 1400)
        }
        imageMobile {
          isImage
          url
          gatsbyImageData(height: 800, width: 800)
        }
        isContinued
      }
      ... on DatoCmsDataSlide {
        ...DatoCmsDataSlide
      }
      ... on DatoCmsBulletsSlide {
        title
        preContent {
          value
        }
        content {
          value
        }
        isContinued
      }
      ... on DatoCmsVideoSlide {
        ...DatoCmsVideoSlide
      }
    }
  }
`
